/**
 * SnackbarProvider — global snackbar state for the app.
 *
 * Extracted from `hooks/identifiers-adapters.ts` (Phase 4d Task 10) so the
 * provider lives alongside the other top-level providers and is no longer
 * coupled to legacy adapter helpers slated for deletion.
 *
 * Public API (unchanged from the previous location):
 *   <SnackbarProvider>{children}</SnackbarProvider>
 *   const { setConfig, updateConfig } = useSnackbar();
 *   const { config, setConfig, updateConfig } = useSnackbarExtended();
 *
 * Render <GlobalSnackbar /> once inside <SnackbarProvider> to display toasts.
 */
import { Snackbar } from "@sage/sage-network-shared-ui/base-components";
import React, { useState, useCallback, createContext, useContext } from "react";

export interface SnackbarConfig {
  open: boolean;
  type: "info" | "success" | "warning" | "error";
  text: string;
  duration?: number;
}

const SnackbarContext = createContext<{
  config: SnackbarConfig;
  setConfig: React.Dispatch<React.SetStateAction<SnackbarConfig>>;
  updateConfig: (partial: Partial<SnackbarConfig>) => void;
}>({
  config: { open: false, type: "info", text: "" },
  setConfig: () => {},
  updateConfig: () => {}
});

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [config, setConfig] = useState<SnackbarConfig>({
    open: false,
    type: "info",
    text: ""
  });

  const updateConfig = useCallback((partial: Partial<SnackbarConfig>) => {
    setConfig((prev) => ({ ...prev, ...partial }));
  }, []);

  return React.createElement(
    SnackbarContext.Provider,
    { value: { config, setConfig, updateConfig } },
    children
  );
};

export const useSnackbar = () => {
  const ctx = useContext(SnackbarContext);
  return { setConfig: ctx.setConfig, updateConfig: ctx.updateConfig };
};

export const useSnackbarExtended = () => {
  const ctx = useContext(SnackbarContext);
  return ctx;
};

/**
 * Renders the shared-library <Snackbar> wired to the global SnackbarContext.
 * Mount this once inside <SnackbarProvider> (e.g. in App.tsx) so every
 * setSnackbarConfig() call in the app produces a visible toast.
 */
export const GlobalSnackbar: React.FC = () => {
  const { config, setConfig } = useSnackbarExtended();

  return (
    <Snackbar
      config={{
        open: config.open,
        text: config.text,
        type: config.type,
        autoHideDuration: config.duration ?? 5000
      }}
      setConfig={(newConfig) =>
        setConfig((prev) => ({ ...prev, open: newConfig.open }))
      }
    />
  );
};
